import { Guid } from './Shared';

export interface LoanApplicationTask {
	guid: Guid;
	actionType: ApplicationActionType;
	taskType: LoanApplicationTaskType;
	creationTriggerType: LoanApplicationTaskCreationTriggerType;
	applicationTaskTitle: string;
	collectionIndex: number | null;
	notApplicable: boolean;
	heading: string;
	subHeading: string;
	displayOrder: number | null;
	completedOn: Date | null;
	completedBy: string | null;
	status: ApplicationTaskStatusType;
	documents: LoanApplicationTaskDocument[];
	createdOn: string;
	summary: string;
	loanApplicationDisplayOrder: number;
	statusValue: number;
	templateFileName: string;
	template: LoanApplicationTaskTemplate;
	hasTemplate: boolean;
}

export type ApplicationTaskStatusType = keyof typeof ApplicationTaskStatusTypeMap;
export const ApplicationTaskStatusTypeMap = {
	Pending: 'Pending',
	InProgress: 'In Progress',
	Completed: 'Completed',
	NotApplicable: 'N/A',
};

export type ApplicationActionType = keyof typeof ApplicationActionTypeMap;
export const ApplicationActionTypeMap = {
	Upload: 'Upload',
	Link: 'Link',
};

export type LoanApplicationTaskType = keyof typeof LoanApplicationTaskTypeMap;
export const LoanApplicationTaskTypeMap = {
	Custom: 'Custom',
	Form1919Section1: 'SBA Form 1919 Section I',
	Form1919Section2: 'SBA Form 1919 Section II',
	Form912: 'SBA Form 912',
	Form413: 'SBA Form 413',
	PersonalRealEstateTitleInsurance: 'Personal Real Estate - Title Insurance',
	PersonalRealEstateVerification: 'Personal Real Estate - Mortgage Verification',
	PersonalRealEstateHazardInsurance: 'Personal Real Estate - Hazard Insurance',
	PersonalRealEstateBrokerPriceOpinion: 'Personal Real Estate - Broker Price Opinion',
	PersonalRealEstateFloodCertifications: 'Personal Real Estate - Flood Certifications',
	PersonalCreditReport: 'Personal Credit Report',
	DerogatoryCreditItemExplanation: 'Explanation of Derogatory Credit Items',
	CopyDriversLicense: 'Copy of Drivers License',
	PersonalTaxReturn: 'Personal Tax Return',
	LastYearW2: 'Last Year W2',
	Form4506T: 'IRS Form 4506-T',
	MostRecentPayStub: 'Most Recent Pay Stubs',
	FranchiseDeclarationDocument: 'Copy of Franchise Declaration Document(s)',
	FranchisorApprovalLetter: 'Franchisor Letter of Approval',
	BusinessTaxReturns: 'Business Tax Returns (Last 3 Years)',
	PreparedCompanyFinancialStatements: 'Business/CPA Prepared Financial Statements',
	InterimBalanceSheet: 'Interim Balance Sheet',
	InterimIncomeStatement: 'Interim Income Statement',
	AccountsReceivableAgingReport: 'A/R Agining Report',
	AccountsPayableAgingReport: 'A/P Aging Report',
	EquipmentList: 'Equipment List',
	WorkingCapitalCertification: 'Working Capital Certification',
	CopyNotesToBeRefinanced: 'Copy of Notes to be Refinanced',
	NextTwelveMonthlyProjections: 'Next Twelve Monthly Projections',
	DetailNextTwelveMonthProjections: 'Detailed Assumptions for Next Twelve Month Projections',
	HistoryOfBusiness: 'History of Business',
	TinVerification: 'Organizational TIN Verification',
	OperatingAgreement: 'Organizational Operating Agreement',
	ArticlesOfOrganization: 'Organizational Articles of Organization',
	CertificateOfMembers: 'Organizational Certificate of Members',
	DbaRegistration: 'Organizational DBA Registration',
	CertificateOfGoodStanding: 'Organizational Certificate of Good Standing',
	SiteInspection: 'Site Inspection',
	FixtureFiling: 'Fixture Filing',
	CopyFullyExpectedLease: 'Lease Agreement - Copy of Full Expected Lease',
	AssignmentLeaseToLender: 'Lease Agreement - Assignment of Lease to Lender',
	LandlordWaiver: 'Lease Agreement - Landlord Waiver',
	AcquisitionPurchaseAgreement: 'Copy of Acquisition Purchase Agreement',
	AcquisitionSellerNote: 'Copy of Acquisition Seller Note',
	AcquisitionBusinessTaxReturns: 'Acquisition Business Tax Returns (Last 3 Years)',
	AcquisitionForm4506T: 'Acquisition IRS Form 4506-T',
	AcquisitionPreparedCompanyFinancialStatements: 'Acquisition Business/CPA Prepared Financial Statements',
	AcquisitionInterimBalanceSheet: 'Acquisition Interim Balance Sheet',
	AcquisitionInterimIncomeStatement: 'Acquisition Interim Income Statement',
	AcquisitionAccountsReceivableAgingReport: 'Acquisition A/R Aging Report',
	AcquisitionAccountsPayableAgingReport: 'Acquisition A/P Aging Report',
	AcquisitionEquipmentList: 'Acquisition Equipment List',
	RefinancePaymentHistory: 'Refinance Payment History',
	RefinancePayoffLetters: 'Refinance Payoff Letters',
	AffiliateBusinessTaxReturns: 'Affiliate Business Tax Returns (Last 3 Years)',
	AffiliateForm4506T: 'Affiliate IRS Form 4506-T',
	AffiliatePreparedCompanyFinancialStatements: 'Affiliate Business/CPA Prepared Financial Statements',
	AffiliateInterimBalanceSheet: 'Affiliate Interim Balance Sheet',
	AffiliateInterimIncomeStatement: 'Affiliate Interim Income Statement',
	AffiliateAccountsReceivableAgingReport: 'Affiliate A/R Aging Report',
	AffiliateAccountsPayableAgingReport: 'Affiliate A/P Aging Report',
	Form202: 'SBA Form 202',
	Form1919Section3: 'SBA Form 1919 Section III',
};

export type LoanApplicationTaskCreationTriggerType = keyof typeof LoanApplicationTaskCreationTriggerTypeMap;
export const LoanApplicationTaskCreationTriggerTypeMap = {
	Other: 'Other',
	DealStructureSelection: 'Deal Structure Selection',
	DealCreation: 'Deal Creation',
};

export interface LoanApplicationTaskDocument {
	guid: Guid;
	documentTitle: string;
	fileName: string;
	createdOn: Date;
	fileSize: number;
}

export interface LoanApplicationTaskTemplate {
	guid: Guid;
	actionType: ApplicationActionType;
	taskType: LoanApplicationTaskType;
	title: string;
	displayOrder: number;
	heading: string;
	subHeading: string;
	canBeAddedManually: boolean;
	canBeDeleted: boolean;
	isLoanApplicationDefault: boolean;
	isCustomFolder: boolean;
	templateFileName: string;
}

export interface CreateRequiredDocumentDto {
	taskType: Guid;
	title: string;
}

export interface UpdateLoanApplicationTaskDto {
	updateType: UpdateLoanApplicationTaskType;
	notApplicable: boolean;
	uploads: File[];
}

export interface UploadLoanApplicationFilesDto {
	uploads: File[];
}

export type UpdateLoanApplicationTaskType = keyof typeof UpdateLoanApplicationTaskTypeMap;
export const UpdateLoanApplicationTaskTypeMap = {
	MarkNa: 'Mark N/A',
	ClearNa: 'Clear N/A',
	Upload: 'Upload',
	Completed: 'Completed',
};
